import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MemoriesPage from './MemoriesPage';
import MemoryWorkspaceLayout from '../components/layouts/MemoryWorkspaceLayout';
import { useLocation } from 'react-router-dom';

import { useSpace } from '../context/SpaceContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogScrollArea,
} from '@/components/ui/dialog';
import { useTranslation } from 'react-i18next';

import { 
  Layers3, 
  Plus, 
  Sparkles, 
  Palette,
  Type,
  Tag
} from 'lucide-react';
import { useAnalytics } from '../hooks/useAnalytics';
import { useTier } from '../contexts/TierContext';
import { useMemoryWorkspaceActions } from '../hooks/useMemoryWorkspaceTracking';
import { UpgradePrompt } from '../components/UpgradePrompt';
import { useWorkspaceLoading } from '../hooks/useWorkspaceLoading';
import { useMemoryMapEnabled } from '../hooks/useMemoryMapEnabled';
import { BILLING_UPGRADE_PATH } from '../constants/urls';
import { rememberWorkspaceSection } from '../lib/workspaceSection';

// Space creation modal component (exported for Storybook)
export function SpaceCreationModal({ isOpen, onClose, onCreateSpace }: {
  isOpen: boolean;
  onClose: () => void;
  onCreateSpace: (data: { name: string; icon: string; color: string; template_type: string }) => Promise<void>;
}) {
  const { t } = useTranslation();
  const { availableSpaces } = useSpace();
  const { getResourceLimits, getCurrentTier, getNextTier } = useTier();
  const [name, setName] = useState('');
  const [selectedIcon, setSelectedIcon] = useState('📁');
  const [selectedColor, setSelectedColor] = useState('#8B5CF6');
  const [selectedTemplate, setSelectedTemplate] = useState('custom');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeUsageInfo, setUpgradeUsageInfo] = useState<{
    current: number;
    limit: number;
    percentage: number;
    warningLevel: 'info' | 'warning' | 'critical' | 'exceeded';
  } | null>(null);

  const spaceIcons = ['📁', '💼', '🎓', '🏠', '🎨', '🔬', '💡', '🎯', '🌟', '🔮', '⚡', '🌈'];
  const spaceColors = [
    '#8B5CF6', '#6366F1', '#EC4899', '#EF4444', '#F59E0B', '#10B981',
    '#06B6D4', '#8B5A2B', '#6B7280', '#7C3AED', '#DC2626', '#059669'
  ];
  const spaceTemplates = [
    { value: 'work' as const, label: t('spaceCreationModal.templates.work.label', { ns: 'dashboard' }), description: t('spaceCreationModal.templates.work.description', { ns: 'dashboard' }) },
    { value: 'personal' as const, label: t('spaceCreationModal.templates.personal.label', { ns: 'dashboard' }), description: t('spaceCreationModal.templates.personal.description', { ns: 'dashboard' }) },
    { value: 'learning' as const, label: t('spaceCreationModal.templates.learning.label', { ns: 'dashboard' }), description: t('spaceCreationModal.templates.learning.description', { ns: 'dashboard' }) },
    { value: 'research' as const, label: t('spaceCreationModal.templates.research.label', { ns: 'dashboard' }), description: t('spaceCreationModal.templates.research.description', { ns: 'dashboard' }) },
    { value: 'creative' as const, label: t('spaceCreationModal.templates.creative.label', { ns: 'dashboard' }), description: t('spaceCreationModal.templates.creative.description', { ns: 'dashboard' }) },
    { value: 'custom' as const, label: t('spaceCreationModal.templates.custom.label', { ns: 'dashboard' }), description: t('spaceCreationModal.templates.custom.description', { ns: 'dashboard' }) },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setError(null); // Clear any previous errors
    setIsCreating(true);
    try {
      await onCreateSpace({
        name: name.trim(),
        icon: selectedIcon,
        color: selectedColor,
        template_type: selectedTemplate,
      });
      
      // Reset form
      setName('');
      setSelectedIcon('📁');
      setSelectedColor('#8B5CF6');
      setSelectedTemplate('custom');
      onClose();
    } catch (error: any) {
      console.error('Failed to create space:', error);
      
      // Handle 409 status (space limit exceeded) - show upgrade modal
      if (error.response?.status === 409) {
        // Get current space limit info for upgrade modal
        try {
          const limitInfo = await getResourceLimits('spaces', availableSpaces.length);
          if (limitInfo) {
            setUpgradeUsageInfo({
              current: limitInfo.limit_status.current_count,
              limit: limitInfo.limit_status.limit || 0,
              percentage: limitInfo.limit_status.percentage_used,
              warningLevel: limitInfo.limit_status.warning_level || 'exceeded'
            });
            // Close creation modal and show upgrade modal
            onClose();
            setShowUpgradeModal(true);
            return;
          }
        } catch (limitError) {
          console.warn('Failed to get limit info for upgrade modal:', limitError);
        }
        
        // Fallback to error message if limit info fetch fails
        const errorMessage = error.response?.data?.detail || t('spaceCreationModal.errorLimit', { ns: 'dashboard' });
        setError(errorMessage);
      } else {
        // Extract error message from backend response for other errors
        let errorMessage = t('spaceCreationModal.errorGeneric', { ns: 'dashboard' });
        if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.message) {
          errorMessage = error.message;
        }
        setError(errorMessage);
      }
    } finally {
      setIsCreating(false);
    }
  };

  // Clear error when modal closes
  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleUpgrade = () => {
    window.open(BILLING_UPGRADE_PATH, '_blank');
    setShowUpgradeModal(false);
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent
          variant="scrollable"
          className="max-w-md border-workspace-primary-500/30 bg-workspace-surface-primary/95 shadow-2xl shadow-workspace-primary-500/20 backdrop-blur-xl"
        >
          <DialogHeader className="flex-shrink-0 p-6 pb-4 pr-12">
            <DialogTitle className="text-workspace-text-primary flex items-center space-x-2">
              <Sparkles className="w-5 h-5 text-workspace-primary-400" />
              <span>{t('spaceCreationModal.title', { ns: 'dashboard' })}</span>
            </DialogTitle>
            <DialogDescription className="text-workspace-text-secondary">
              {t('spaceCreationModal.description', { ns: 'dashboard' })}
            </DialogDescription>
          </DialogHeader>

          <DialogScrollArea
            className="pr-1 px-6"
            aria-label={t('spaceCreationModal.title', { ns: 'dashboard' })}
          >
            <div className="space-y-6">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <form id="space-creation-form" onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-workspace-text-secondary text-sm font-medium flex items-center space-x-2">
                    <Type className="w-4 h-4" />
                    <span>{t('spaceCreationModal.nameLabel', { ns: 'dashboard' })}</span>
                  </label>
                  <Input
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      if (error) setError(null);
                    }}
                    placeholder={t('spaceCreationModal.namePlaceholder', { ns: 'dashboard' })}
                    className="bg-workspace-surface-secondary/50 border-workspace-primary-500/20
                             text-workspace-text-primary placeholder:text-workspace-text-tertiary
                             focus:border-workspace-primary-400/50 focus:ring-workspace-primary-400/20"
                    maxLength={50}
                    required
                  />
                </div>

                <div className="space-y-3">
                  <label className="text-workspace-text-secondary text-sm font-medium flex items-center space-x-2">
                    <Sparkles className="w-4 h-4" />
                    <span>{t('spaceCreationModal.chooseIcon', { ns: 'dashboard' })}</span>
                  </label>
                  <div className="grid grid-cols-6 gap-2">
                    {spaceIcons.map((icon) => (
                      <button
                        key={icon}
                        type="button"
                        onClick={() => setSelectedIcon(icon)}
                        className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg
                                   transition-all duration-200 hover:scale-110
                                   ${selectedIcon === icon
                                     ? 'bg-workspace-primary-500/30 ring-2 ring-workspace-primary-400'
                                     : 'bg-workspace-surface-secondary/30 hover:bg-workspace-surface-secondary/50'
                                   }`}
                      >
                        {icon}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-workspace-text-secondary text-sm font-medium flex items-center space-x-2">
                    <Palette className="w-4 h-4" />
                    <span>{t('spaceCreationModal.chooseColor', { ns: 'dashboard' })}</span>
                  </label>
                  <div className="grid grid-cols-6 gap-2">
                    {spaceColors.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setSelectedColor(color)}
                        className={`w-10 h-10 rounded-lg transition-all duration-200 hover:scale-110
                                   ${selectedColor === color
                                     ? 'ring-2 ring-workspace-primary-400'
                                     : 'ring-1 ring-inset ring-white/20'
                                   }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="text-workspace-text-secondary text-sm font-medium flex items-center space-x-2">
                    <Tag className="w-4 h-4" />
                    <span>{t('spaceCreationModal.templateType', { ns: 'dashboard' })}</span>
                  </label>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {spaceTemplates.map((template) => (
                      <button
                        key={template.value}
                        type="button"
                        onClick={() => setSelectedTemplate(template.value)}
                        className={`p-3 text-left rounded-lg transition-all duration-200 border
                                   ${selectedTemplate === template.value
                                     ? 'bg-workspace-primary-500/30 ring-2 ring-workspace-primary-400 border-transparent'
                                     : 'bg-workspace-surface-secondary/30 border-workspace-surface-secondary/50 hover:bg-workspace-surface-secondary/50'
                                   }`}
                      >
                        <div className="text-workspace-text-primary font-medium text-sm">
                          {template.label}
                        </div>
                        <div className="text-workspace-text-tertiary text-xs mt-1">
                          {template.description}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </form>
            </div>
          </DialogScrollArea>

          <DialogFooter
            variant="scrollable"
            className="flex-shrink-0 flex-col-reverse gap-2 border-workspace-primary-500/20 bg-workspace-surface-primary/95 px-6 pb-6 pt-4 backdrop-blur-xl sm:flex-row sm:justify-end"
          >
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 border-workspace-primary-500/40 bg-workspace-surface-secondary/40 text-workspace-text-primary
                       hover:bg-workspace-surface-secondary/60 hover:text-workspace-text-primary sm:flex-initial
                       focus-visible:ring-workspace-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-workspace-surface-primary"
            >
              {t('spaceCreationModal.cancel', { ns: 'dashboard' })}
            </Button>
            <Button
              type="submit"
              form="space-creation-form"
              disabled={!name.trim() || isCreating}
              className="flex-1 bg-workspace-primary-500 text-white shadow-lg shadow-workspace-primary-500/30
                       hover:bg-workspace-primary-600 focus-visible:ring-workspace-primary-400 focus-visible:ring-offset-2
                       focus-visible:ring-offset-workspace-surface-primary sm:flex-initial"
            >
              {isCreating ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                t('spaceCreationModal.createButton', { ns: 'dashboard' })
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    {/* Upgrade Prompt Modal */}
    <UpgradePrompt
      isOpen={showUpgradeModal}
      onClose={() => setShowUpgradeModal(false)}
      onUpgrade={handleUpgrade}
      context="space_limit"
      currentTier={getCurrentTier()}
      nextTier={getNextTier() || 'pro'}
      usageInfo={upgradeUsageInfo || undefined}
    />
    </>
  );
}

// Main Spaces Page component with spatial wrapper
function SpacesPageContent() {
  const { pathname } = useLocation();
  const analytics = useAnalytics();
  const { enabled: memoryMapEnabled } = useMemoryMapEnabled();
  const { currentSpace, createSpace, isTransitioningSpaces } = useSpace();
  const workspaceLoading = useWorkspaceLoading({
    spaceId: currentSpace?.space_id || null,
    enabled: !!currentSpace
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [_showManageModal, _setShowManageModal] = useState(false);

  // Workspace activity tracking
  const workspaceActions = useMemoryWorkspaceActions();

  useEffect(() => {
    analytics.trackEvent('page_view', {
      page: 'spaces',
      realm: 'sanctuary'
    });
  }, [analytics]);

  useEffect(() => {
    rememberWorkspaceSection('memories');
  }, []);

  const handleCreateSpace = async (spaceData: { name: string; icon: string; color: string; template_type: string }) => {
    await createSpace(spaceData);
    
    // PostHog analytics
    analytics.trackEvent('space_created', {
      template_type: spaceData.template_type,
      has_custom_icon: spaceData.icon !== '📁',
      has_custom_color: spaceData.color !== '#8B5CF6',
    });
    
    // Activity tracking
    workspaceActions.onSpaceCreated({
      name: spaceData.name,
      template_type: spaceData.template_type
    });
  };

  return (
    <MemoryWorkspaceLayout
      onCreateSpace={() => setShowCreateModal(true)}
      onManageSpaces={() => _setShowManageModal(true)}
      workspaceAccentColor={currentSpace?.color ?? null}
      sectionNav={{
        current: 'memories',
        memoriesHref: pathname.startsWith('/workspace') ? '/workspace' : '/spaces/memories',
        mapHref: pathname.startsWith('/workspace') ? '/workspace/map' : '/spaces/map',
        showMap: memoryMapEnabled,
      }}
    >
      <div className="relative min-h-screen">
      {/* Main Content */}
      <div className="relative z-10">
        {/* Space transition overlay */}
        <AnimatePresence>
          {isTransitioningSpaces && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-30 bg-workspace-surface-primary/50 backdrop-blur-lg
                         flex items-center justify-center"
            >
              <div className="text-center space-y-4">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  className="w-12 h-12 border-4 border-workspace-primary-500 border-t-transparent
                           rounded-full mx-auto"
                />
                <div className="text-workspace-text-primary font-medium">
                  Entering space...
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Content within spatial context */}
        <motion.div
          key={currentSpace?.space_id || 'no-space'}
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.98 }}
          transition={{
            type: 'spring',
            damping: 25,
            stiffness: 300,
          }}
          className="relative"
          style={{ 
            perspective: '1000px',
            transformStyle: 'preserve-3d',
          }}
        >
          {/* Unified loading state check */}
          {workspaceLoading.isSpacesLoading ? (
            <div className="w-full py-12 text-center">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-workspace-text-tertiary"
              >
                <div className="w-12 h-12 border-4 border-workspace-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <h2 className="text-xl font-medium text-workspace-text-primary mb-2">
                  Loading Spaces...
                </h2>
                <p className="text-workspace-text-secondary">
                  Please wait while we load your memory spaces
                </p>
              </motion.div>
            </div>
          ) : currentSpace ? (
            <div className="w-full pt-4 pb-8">
              {/* Memory Interface - only render when spaces are loaded */}
              <MemoriesPage spaceContext={currentSpace} />
            </div>
          ) : (
            <div className="w-full py-12 text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2 }}
                className="text-workspace-text-tertiary"
              >
                <Layers3 className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <h2 className="text-xl font-medium text-workspace-text-primary mb-2">
                  Welcome to the Memory Workspace
                </h2>
                <p className="text-workspace-text-secondary mb-6">
                  Create your first space to begin organizing your memories within the Memory Workspace
                </p>
                <Button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-workspace-primary-500 hover:bg-workspace-primary-600 text-white
                           shadow-lg shadow-workspace-primary-500/30"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Space
                </Button>
              </motion.div>
            </div>
          )}
        </motion.div>
      </div>

        {/* Modals */}
        <SpaceCreationModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCreateSpace={handleCreateSpace}
        />
      </div>
    </MemoryWorkspaceLayout>
  );
}

// Main exported component - now uses provider from higher level
export default function SpacesPage() {
  return <SpacesPageContent />;
} 